// ⬡B:aba_shared.ccwa_core.components.widget_rail:CODE:fix5_content_height_gear_position:20260615⬡
// WidgetRail — right-side widget column for CCWA portal.
// Fix 5: cards shrink to fit content instead of stretching to fill the rail.
//   - cardStyle uses height:'auto' / flex:0 0 auto so an empty or short widget
//     never produces a tall blank box.
//   - elementStyle caps at maxHeight:320px with scroll so tall content is still
//     contained.
//   - Settings gear is anchored to the WidgetRail outer container header area
//     (top-right of the rail, not inside any card corner) so it never overlaps
//     a widget panel corner.
// Frosted glass cards, paged two per view, swipe + arrow navigation.
// ALL styles inline — no external stylesheet dependency.

import React, { useState, useRef } from 'react';

export function WidgetRail({ widgets = [], className = '', style = {}, onSettings }) {
  const [currentPage, setCurrentPage] = useState(0);
  const widgetsPerPage = 2;
  const totalPages = Math.max(1, Math.ceil(widgets.length / widgetsPerPage));
  const startIndex = currentPage * widgetsPerPage;
  const currentWidgets = widgets.slice(startIndex, startIndex + widgetsPerPage);

  const goToPage = (page) => {
    if (page >= 0 && page < totalPages) setCurrentPage(page);
  };

  const touchStartX = useRef(0);
  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (diff > 50) goToPage(currentPage + 1);
    else if (diff < -50) goToPage(currentPage - 1);
  };

  const font = 'system-ui, -apple-system, sans-serif';

  // Outer shell — position:relative so the gear button anchors to this corner.
  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    position: 'relative',
    background: 'linear-gradient(135deg, rgba(255,255,255,0.10) 0%, rgba(14,20,40,0.50) 46%, rgba(8,12,24,0.56) 100%)',
    backdropFilter: 'blur(2px) saturate(165%) brightness(1.04)',
    WebkitBackdropFilter: 'blur(2px) saturate(165%) brightness(1.04)',
    borderRadius: '18px',
    border: '1px solid rgba(255,255,255,0.10)',
    boxShadow: '0 10px 40px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.22), inset 0 0 0 1px rgba(255,255,255,0.04)',
    overflow: 'visible',  // allow gear to sit outside without clipping
    boxSizing: 'border-box',
    padding: '12px',
    gap: '10px',
    ...style,
  };

  // Content wrapper — no longer flex:1 so it doesn't force-stretch
  const contentStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  };

  // Card shrinks to content. No flex:1.
  const cardStyle = {
    flex: '0 0 auto',
    background: 'rgba(20, 30, 60, 0.65)',
    backdropFilter: 'blur(2px)',
    WebkitBackdropFilter: 'blur(2px)',
    borderRadius: '13px',
    border: '1px solid rgba(255,255,255,0.10)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxSizing: 'border-box',
  };

  const titleBarStyle = {
    flexShrink: 0,
    padding: '8px 12px 7px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.10em',
    textTransform: 'uppercase',
    color: 'rgba(120,200,255,0.80)',
    fontFamily: font,
    background: 'rgba(0,0,0,0.18)',
  };

  // Element area: height auto, capped at 320px with scroll so content fits
  // naturally but never overflows unbounded.
  const elementStyle = {
    overflowY: 'auto',
    maxHeight: '320px',
    padding: '10px 12px',
    color: 'rgba(255,255,255,0.82)',
    fontSize: '13px',
    fontFamily: font,
  };

  const emptyRailStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'rgba(255,255,255,0.28)',
    fontSize: '13px',
    fontFamily: font,
    padding: '24px 0',
  };

  const paginationStyle = {
    display: totalPages > 1 ? 'flex' : 'none',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    paddingTop: '4px',
    flexShrink: 0,
  };

  const arrowBtnStyle = (disabled) => ({
    background: disabled ? 'rgba(255,255,255,0.06)' : 'rgba(60,160,220,0.25)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '8px',
    color: disabled ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.80)',
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: disabled ? 'default' : 'pointer',
    fontSize: '13px',
    transition: 'background 0.15s',
    padding: 0,
    outline: 'none',
  });

  const dotStyle = (active) => ({
    width: active ? '18px' : '6px',
    height: '6px',
    borderRadius: '3px',
    background: active ? 'rgba(80,190,255,0.9)' : 'rgba(255,255,255,0.25)',
    cursor: 'pointer',
    transition: 'width 0.2s, background 0.2s',
    display: 'inline-block',
  });

  // Settings gear — anchored to the top-right of the rail container,
  // outside the card stack. Uses position:absolute relative to containerStyle
  // (which has position:relative and overflow:visible).
  const gearStyle = {
    position: 'absolute',
    top: '10px',
    right: '10px',
    width: '26px',
    height: '26px',
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.12)',
    color: 'rgba(255,255,255,0.45)',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: onSettings ? 'pointer' : 'default',
    fontFamily: font,
    lineHeight: 1,
    zIndex: 2,
    transition: 'background 0.15s, color 0.15s',
    // Sits above the title bar text but does not overlap any card panel corner:
    // it lives in the 12px padding zone at the top of the rail.
  };

  return (
    <div
      className={['widget-rail', className].filter(Boolean).join(' ')}
      style={containerStyle}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Settings gear — positioned in the rail header zone, never inside a card */}
      {onSettings && (
        <button
          style={gearStyle}
          onClick={onSettings}
          aria-label="Widget settings"
          title="Settings"
        >
          &#9881;
        </button>
      )}

      <div style={contentStyle}>
        {currentWidgets.length === 0 ? (
          <div style={emptyRailStyle}>No widgets</div>
        ) : (
          currentWidgets.map((widget) => (
            <div key={widget.id} style={cardStyle}>
              <div style={titleBarStyle}>
                {widget.title || widget.id || 'Widget'}
              </div>
              <div style={elementStyle}>
                {widget.element ?? widget.component ?? null}
              </div>
            </div>
          ))
        )}
      </div>

      <div style={paginationStyle}>
        <button
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 0}
          style={arrowBtnStyle(currentPage === 0)}
          aria-label="Previous page"
        >
          &#8249;
        </button>
        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
          {Array.from({ length: totalPages }).map((_, idx) => (
            <span
              key={idx}
              style={dotStyle(idx === currentPage)}
              onClick={() => goToPage(idx)}
              role="button"
              aria-label={`Page ${idx + 1}`}
            />
          ))}
        </div>
        <button
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === totalPages - 1}
          style={arrowBtnStyle(currentPage === totalPages - 1)}
          aria-label="Next page"
        >
          &#8250;
        </button>
      </div>
    </div>
  );
}

export default WidgetRail;
